import { Component, HostListener, OnInit, computed, effect, inject, untracked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ErrorStateService } from './core/error-state.service';
import { GmrDataService } from './core/gmr-data.service';
import { HashStateService } from './core/hash-state.service';
import { DownloadService } from './core/download.service';
import { LoadingService } from './core/loading.service';
import { UiStateService } from './core/ui-state.service';
import { FunctionPickerComponent } from './function-picker/function-picker.component';
import { SidebarComponent } from './function-picker/sidebar.component';
import { ParameterFormComponent } from './parameter-form/parameter-form.component';
import { ErrorModalComponent } from './shared/components/error-modal.component';
import { DownloadListComponent } from './shared/components/download-list.component';
import { SpinnerOverlayComponent } from './shared/components/spinner-overlay.component';
import { TopNavComponent } from './top-nav/top-nav.component';
import { UserSettingsModalComponent } from './top-nav/user-settings-modal.component';

//
// 1:1 port of the <body> structure in menu.html (site/menu.html), root shell only - the actual
// function-picker/parameter-form content lives in their own components.
//
@Component({
  selector: 'app-root',
  standalone: true,
  imports:
  [
    TopNavComponent
  , UserSettingsModalComponent
  , SidebarComponent
  , FunctionPickerComponent
  , ParameterFormComponent
  , ErrorModalComponent
  , DownloadListComponent
  , SpinnerOverlayComponent
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
  //
  // Three attributes on <app-root> describing what the app is doing, added 2026-08-14. Before that
  // there was no signal at all: the only way to tell an app that was still loading from one that had
  // finished - or from one that had silently hung - was to look at the pixels and guess, so every
  // check had to be preceded by an arbitrary sleep. That converts a real defect into a flaky test and
  // hides the failure mode described in KNOWN_ISSUES (a 200 carrying HTML leaves the app on "Waiting
  // for data" for ever, with nothing in the console).
  //
  // Attributes rather than classes: they carry a value, they are not styled by anything, and nothing
  // in the legacy stylesheets can collide with them.
  //
  // data-app-downloads counts the UNFINISHED entries, queued ones included - not the ones in flight.
  // A queued download holds no connection and no bytes, and conflating the two makes a queue look like
  // a leak. The phase of each download sits on its own row as data-download-state, because a single
  // value on the root cannot express two downloads in different phases.
  //
  host:
  {
    '[attr.data-app-state]': 'appState()'
  , '[attr.data-app-step]': 'ui.step()'
  , '[attr.data-app-preset]': 'data.selectedFunction()?.cfn_preset_name ?? null'
  , '[attr.data-app-downloads]': 'downloads.unfinishedCount() || null'
  },
})
export class App implements OnInit
{
  protected readonly data = inject(GmrDataService);
  protected readonly ui = inject(UiStateService);
  protected readonly errorState = inject(ErrorStateService);
  protected readonly loading = inject(LoadingService);
  protected readonly downloads = inject(DownloadService);
  private readonly hashState = inject(HashStateService);
  private readonly translate = inject(TranslateService);

  private hasRestoredFromHash = false;

  //
  // The four states are ordered by how much they override each other, most decisive first:
  //
  //   error   - an error modal is on screen. Says so first, because an error can perfectly well be
  //             raised while something else is still in flight, and "there is an error" is the fact
  //             that matters then. A FAILED DOWNLOAD does not put the app here (decided 2026-08-14):
  //             it is shown as a failed row, no request is outstanding, and the screen is final - so
  //             `ready` beside a visible failure is deliberate, not an oversight.
  //   loading - the report catalogue has not settled yet. This is the state the app boots into and
  //             the one it is stuck in when the catalogue never arrives, which is precisely the
  //             failure this attribute is meant to make visible.
  //   busy    - the catalogue is up and the app is usable, but a request or a download is running.
  //             Distinct from `loading` so that "still starting up" and "working on something I asked
  //             for" are not the same word. It stays until the LAST download settles, not the first.
  //   ready   - idle and operable. The only state in which the screen can be trusted to be final.
  //
  protected readonly appState = computed(() =>
  {
    if (this.errorState.errorModal().visible) return 'error';
    if (this.loading.awaitingCatalog()) return 'loading';
    if (this.loading.isLoading() || this.downloads.hasUnfinished()) return 'busy';
    return 'ready';
  });

  constructor()
  {
    //
    // 1:1 port of the declarative <title translate="{{ ... }}"> binding on <title> in menu.html.
    // The original ALSO has imperative document.title mutations (with a duplicate-suffix quirk)
    // in startParameters/onLanguageChange, run via plain setTimeout - which falls outside
    // AngularJS' digest cycle, so those imperative writes only survive until the next digest
    // re-evaluates this binding and overwrites them again. Given how frequently AngularJS digests
    // run (any $http/$timeout resolution), that's likely near-invisible in practice; reproducing
    // the exact timing race isn't meaningful in a signals-based app, so only this declarative
    // binding is ported.
    //
    effect(() =>
    {
      const fn = this.data.selectedFunction();
      const key = fn?.cfn_description || null;
      if (key)
      {
        this.translate.get(key).subscribe(value => document.title = value);
      }
      else
      {
        this.translate.get('Get My Report for Exact Online | Invantive').subscribe(value => document.title = value);
      }
    });
    //
    // Keep <html lang> on the language actually being rendered. index.html hard-codes lang="en" and
    // nothing ever changed it, so the document claimed English while the interface was Dutch - or
    // German, Spanish or French - from the moment the user's profile language was applied.
    //
    // This is a WCAG 2.2 SC 3.1.1 failure at level A, and the consequence is concrete rather than
    // formal: a screen reader picks its pronunciation rules from this attribute, so it read correct
    // Dutch aloud with English phonetics. Fully translated and unintelligible at the same time.
    //
    // Driven off onLangChange rather than set once, because the language changes twice in a normal
    // session: GmrDataService applies the profile language when the catalogue arrives, and the user
    // can switch it again in the top bar. Only the primary subtag is written (`nl`, not `nl-NL`),
    // matching what translate.use() is given.
    //
    const applyDocumentLanguage = (lang: string | null | undefined) =>
    {
      if (lang)
      {
        document.documentElement.lang = lang;
      }
    };

    applyDocumentLanguage(this.translate.getCurrentLang() ?? this.translate.getFallbackLang());
    this.translate.onLangChange.subscribe(event => applyDocumentLanguage(event.lang));

    //
    // Port of executeHash() in startFunction's success handler: once functions load, if the URL hash
    // names a preset, select it and restore any bookmarked division_codes.
    //
    // applyHash() runs untracked because it reads selectedFunction() and step() to decide what to do.
    // Without untracked those reads would become dependencies of this effect, so selecting a function
    // would re-trigger the very effect that selected it.
    //
    effect(() =>
    {
      const functions = this.data.myFunctions();
      if (functions.length === 0 || this.hasRestoredFromHash) return;
      this.hasRestoredFromHash = true;
      untracked(() => this.applyHash());
    });

    //
    // Port of window.addEventListener("hashchange", executeHash) - and the fix for the browser's Back
    // button, which did nothing at all before 2026-08-13.
    //
    // What made it dead: a report is opened through a plain <a href="#preset=..."> in the function
    // list, so the browser puts a real entry on the history stack and Back genuinely returns to
    // /menu.html. The URL changed, the app did not. This listener used to only set
    // `hasRestoredFromHash = false`, an ordinary field rather than a signal, so nothing re-ran: the
    // effect above depends on myFunctions(), which had not changed, and a plain field assignment
    // cannot invalidate it. Calling applyHash() directly is what closes that gap.
    //
    window.addEventListener('hashchange', () => this.applyHash());
  }

  ngOnInit(): void
  {
    this.data.startFunction();
  }

  //
  // Make the screen match the URL hash, whatever caused it to change: the initial load, a click on a
  // report in the list, a bookmarked link, or the browser's Back/Forward buttons.
  //
  // An empty hash means the overview. That is safe to act on because the parameter form carries a
  // hidden `preset` input, so the bookmark hash that writeBookmark() serialises out of the form keeps
  // `preset=` in it - checked before relying on it. Were that not so, downloading a report would
  // rewrite the hash without a preset and this would throw the user back to the overview mid-task.
  //
  // Doing nothing when the preset is unknown is deliberate: an unrecognised bookmark should leave the
  // screen as it is rather than silently dumping the user somewhere they did not ask to be.
  //
  private applyHash(): void
  {
    const functions = this.data.myFunctions();
    if (functions.length === 0) return;

    const { preset, divisionCodes } = this.hashState.parsePresetAndDivisions();

    if (!preset)
    {
      this.ui.showFunctionPicker();
      return;
    }

    const fn = functions.find(f => f.cfn_preset_name === preset);
    if (!fn) return;

    //
    // Already showing exactly this report: stop here. Re-running selectFunction() would reload the
    // list-of-values and re-fire the "navigated to report" analytics event for a navigation that did
    // not happen - which is what writeBookmark() would cause on every download, since it writes the
    // serialised form back into the hash and that fires hashchange.
    //
    if (this.data.selectedFunction()?.cfn_preset_name === preset && this.ui.step() === 'parameterForm')
    {
      return;
    }

    const shown = this.data.selectFunction(fn);
    if (shown)
    {
      this.ui.goToParameterForm(fn.cfn_preset_name);
    }
    if (divisionCodes.length > 0)
    {
      this.data.restoreDivisionCodes(divisionCodes);
    }
  }

  //
  // 1:1 port of document.onkeydown's Escape handler.
  //
  @HostListener('document:keydown.escape')
  onEscape(): void
  {
    this.ui.handleEscapeKey();
  }
}
